import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, UserRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PortalLogin } from "../components/portal/PortalLogin";
import { MembershipCard } from "../components/portal/MembershipCard";
import { VehiclesSection } from "../components/portal/VehiclesSection";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { NotificationBell } from "../components/NotificationBell";
import { PushSettings } from "../components/PushSettings";
import { PortalAppointments, NotificationSettings } from "../components/portal/PortalNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

const ADDRESS_FIELDS = [
  ["phone", "Mobile Phone"], ["street", "Street Address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"],
];

const ProfileCard = ({ user, onSaved }) => {
  const [form, setForm] = useState({
    phone: user.phone || "", street: user.street || "", city: user.city || "",
    state: user.state || "", zip: user.zip || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/auth/profile`, form, { withCredentials: true });
      toast.success("Profile updated");
      onSaved(data);
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-[#121212] border border-zinc-800 rounded-xl p-8" data-testid="portal-profile-card">
      <div className="flex items-center gap-3 mb-6">
        <UserRound className="h-5 w-5 text-blue-500" />
        <h3 className="font-heading text-lg font-semibold text-white">Contact &amp; Service Address</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {ADDRESS_FIELDS.map(([id, label]) => (
          <div key={id} className="space-y-1">
            <Label className="text-zinc-400 text-xs">{label}</Label>
            <Input
              data-testid={`profile-input-${id}`}
              className={inputCls}
              value={form[id]}
              onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <button
        data-testid="profile-save-button"
        type="submit"
        disabled={saving}
        className="mt-5 inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-5 py-2 text-sm font-semibold transition-colors"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
      </button>
    </form>
  );
};

export default function Portal() {
  const { user, setUser, logout } = useAuth();
  const [membership, setMembership] = useState(null);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [memRes, vehRes] = await Promise.all([
        axios.get(`${API}/membership/me`, { withCredentials: true }),
        axios.get(`${API}/vehicles`, { withCredentials: true }),
      ]);
      setMembership(memRes.data.membership);
      setServices(memRes.data.complimentary_services);
      setVehicles(vehRes.data);
    } catch {
      toast.error("Could not load your account data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  if (user === null) {
    return (
      <main data-testid="portal-page" className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </main>
    );
  }

  if (!user) {
    return (
      <main data-testid="portal-page" className="pt-20 md:pt-24 min-h-screen">
        <PortalLogin />
      </main>
    );
  }

  return (
    <main data-testid="portal-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="portal-greeting">
              Welcome, {user.first_name || user.name || "Member"}
            </h1>
            <p className="text-zinc-500 text-sm mt-1">{user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              data-testid="portal-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white rounded-sm px-4 py-2 text-sm font-semibold transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
        ) : (
          <div className="space-y-6">
            <PortalAppointments />
            <MembershipCard membership={membership} services={services} vehicles={vehicles} onRefresh={fetchData} />
            <VehiclesSection vehicles={vehicles} onChanged={fetchData} />
            <ProfileCard user={user} onSaved={setUser} />
            <PushSettings />
            <NotificationSettings user={user} />
          </div>
        )}
      </div>
    </main>
  );
}
